import {useEffect, useRef} from 'react';
import { initAnalytics, setAnalyticsCollectionEnabled, trackEvent } from '../utils/analytics';

const enabled = () => process.env.NODE_ENV === 'production' && process.env.REACT_APP_COMMUNITY_DEMO !== 'true';
let started = false;
let active = true;
let source = 'website';
const entryContext = entryPoint => ({
  entry_point: entryPoint === 'latest_sightings_app' ? 'latest_sightings_app' : 'website',
  game_surface: entryPoint === 'latest_sightings_app' ? 'App' : 'Website',
});

// Preserve campaign attribution without forwarding unrelated URL parameters
// (for example highlight IDs or authentication tokens) into page-view URLs.
function campaignSearch() {
  const incoming = new URLSearchParams(window.location.search);
  const campaign = new URLSearchParams();
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_content', 'utm_term']) {
    const value = incoming.get(key);
    if (value) campaign.set(key, value);
  }
  const query = campaign.toString();
  return query ? `?${query}` : '';
}

export function startCommunityAnalytics(entryPoint = 'website') {
  source = entryContext(entryPoint).entry_point;
  active = true;
  if (enabled()) setAnalyticsCollectionEnabled(true);
  if (!enabled() || started) return false;
  started = true;
  // Website routes and embedded app screens send one explicit page
  // view when the actual authenticated screen opens, not another automatic one.
  initAnalytics({gtagOptions: {send_page_view: false}});
  trackEvent('community_session_started', {
    app_area: 'community_review',
    ...entryContext(source),
    device_layout: window.matchMedia?.('(max-width: 760px)').matches ? 'mobile' : 'desktop',
  });
  return true;
}

// A standby WebView prepares clips without recording a visit or game activity.
export function setCommunityAnalyticsActive(value) {
  active = value === true;
  if (enabled()) setAnalyticsCollectionEnabled(active);
}

export function trackCommunity(eventName, parameters = {}) {
  if (!enabled() || !started || !active) return;
  trackEvent(eventName, { app_area: 'community_review', ...parameters,
    ...entryContext(parameters.entry_point || source) });
}

export function trackCommunityPage(view, entryPoint = 'website') {
  if (!enabled() || !started || !active || !['review', 'highlights'].includes(view)) return false;
  const path = entryPoint === 'website' ? (view === 'highlights' ? '/highlights' : '/') : `/community-review/${view}`;
  const title = `${entryContext(entryPoint).game_surface} | ${view === 'highlights' ? 'Highlights' : 'Review'} | Ting Vision`;
  document.title = title;
  trackCommunity('page_view', {
    page_title: title,
    page_path: path,
    page_location: `${window.location.origin}${path}${campaignSearch()}`,
    community_page: view,
    entry_point: entryPoint,
  });
  return true;
}

export function useCommunityPageView(view, entryPoint, active = true) {
  const previous = useRef(null);
  useEffect(() => {
    if (!active) { previous.current = null; return; }
    const key = `${entryPoint}:${view}`;
    if (previous.current !== key && trackCommunityPage(view, entryPoint)) previous.current = key;
  }, [active, entryPoint, view]);
}

export function analyticsErrorArea(error, fallback = 'unknown') {
  const code = typeof error?.code === 'string' ? error.code : '';
  return code ? code.replace(/^.*\//, '').replace(/[^a-z0-9_-]/gi, '_').slice(0, 40) : fallback;
}

export function __resetCommunityAnalyticsForTests() { started = false; active = true; source = 'website'; }
